// ============================================================
//  ALTIMO Medical Center — ActivosFijos.jsx
//  Ubicación: src/components/ActivosFijos.jsx
//  Funcionalidades:
//    ✓ Catálogo de activos con valor en libros proyectado (línea recta)
//    ✓ Alta / edición de activo
//    ✓ Kardex físico (traslado, mantenimiento, ajuste)
//    ✓ Baja / venta con cálculo de utilidad o pérdida
//    ✓ Cierre mensual de depreciación (snapshot contable oficial)
//    ✓ Reporte de depreciación por período, exportable a Excel/PDF
// ============================================================

import { useState, useEffect, useCallback } from 'react';
import api from '../apiClient';

// ── Paleta y estilos (idéntico a Inventario.jsx para consistencia visual) ──
const C = {
  azul:    '#1B2A5E',
  verde:   '#1D9E75',
  rojo:    '#dc3545',
  naranja: '#e67e22',
  gris:    '#6c757d',
  morado:  '#6f42c1',
};

const S = {
  pagina:    { background:'#f0f4f8', minHeight:'100vh', padding:'20px', fontFamily:'Segoe UI, sans-serif' },
  card:      { background:'#fff', borderRadius:'10px', border:'1px solid #dde3ea', padding:'18px', marginBottom:'16px' },
  cardTit:   { fontSize:'12px', fontWeight:'700', color:C.azul, textTransform:'uppercase', letterSpacing:'.05em',
               marginBottom:'14px', paddingBottom:'10px', borderBottom:'2px solid #e8f0fb',
               display:'flex', justifyContent:'space-between', alignItems:'center', flexWrap:'wrap', gap:'10px' },
  tabla:     { width:'100%', borderCollapse:'collapse', fontSize:'13px' },
  th:        { textAlign:'left', padding:'8px 10px', fontSize:'11px', color:C.gris, fontWeight:'600',
               borderBottom:'2px solid #e8f0fb', background:'#f8fafd', whiteSpace:'nowrap' },
  td:        { padding:'9px 10px', borderBottom:'1px solid #eef1f6', color:'#333', verticalAlign:'middle' },
  input:     { width:'100%', fontSize:'13px', padding:'8px 10px', border:'1px solid #c0ccda',
               borderRadius:'7px', background:'#fff', color:'#333', outline:'none', boxSizing:'border-box' },
  label:     { display:'block', fontSize:'11px', fontWeight:'600', color:C.gris,
               marginBottom:'4px', textTransform:'uppercase', letterSpacing:'.04em' },
  campo:     { marginBottom:'12px' },
  fila2:     { display:'grid', gridTemplateColumns:'1fr 1fr', gap:'12px' },
  fila3:     { display:'grid', gridTemplateColumns:'1fr 1fr 1fr', gap:'12px' },
  btnVerde:  { background:C.verde, color:'#fff', border:'none', borderRadius:'7px',
               padding:'8px 18px', fontSize:'13px', fontWeight:'700', cursor:'pointer' },
  btnAzul:   { background:C.azul, color:'#fff', border:'none', borderRadius:'7px',
               padding:'7px 14px', fontSize:'12px', fontWeight:'600', cursor:'pointer' },
  btnGris:   { background:'#fff', color:'#495057', border:'1px solid #c0ccda',
               borderRadius:'7px', padding:'7px 14px', fontSize:'12px', cursor:'pointer' },
  btnNaranja:{ background:C.naranja, color:'#fff', border:'none', borderRadius:'7px',
               padding:'7px 14px', fontSize:'12px', fontWeight:'600', cursor:'pointer' },
  btnRojo:   { background:C.rojo, color:'#fff', border:'none', borderRadius:'7px',
               padding:'7px 14px', fontSize:'12px', fontWeight:'600', cursor:'pointer' },
  btnSm: (color) => ({
    background: color || '#e8f0fb', color: color ? '#fff' : C.azul,
    border:'none', borderRadius:'5px', padding:'4px 9px', fontSize:'11px',
    fontWeight:'600', cursor:'pointer', whiteSpace:'nowrap',
  }),
  alerta: (tipo) => ({
    padding:'10px 14px', borderRadius:'8px', fontSize:'13px', fontWeight:'500', marginBottom:'12px',
    background: tipo==='ok'?'#d4edda': tipo==='err'?'#f8d7da':'#fff3cd',
    color:      tipo==='ok'?'#155724': tipo==='err'?'#721c24':'#856404',
    border:     `1px solid ${tipo==='ok'?'#c3e6cb': tipo==='err'?'#f5c6cb':'#ffc107'}`,
  }),
  modal:     { position:'fixed', top:0, left:0, right:0, bottom:0, background:'rgba(0,0,0,.45)',
               display:'flex', alignItems:'center', justifyContent:'center', zIndex:1000, overflowY:'auto' },
  modalCard: (w) => ({ background:'#fff', borderRadius:'12px', padding:'28px', width: w||'480px',
                       border:'1px solid #dde3ea', maxHeight:'90vh', overflowY:'auto', margin:'auto' }),
  badge: (color) => ({
    display:'inline-block', padding:'2px 8px', borderRadius:'99px', fontSize:'10px', fontWeight:'700',
    background: color+'22', color, border:`1px solid ${color}44`,
  }),
  tabBtn: (activo) => ({
    padding:'8px 18px', borderRadius:'7px', fontSize:'13px', fontWeight:'600',
    cursor:'pointer', border:'none',
    background: activo ? C.azul : '#e8f0fb',
    color:      activo ? '#fff' : C.azul,
  }),
  totalBox: { background:C.azul, borderRadius:'10px', padding:'14px 20px',
              display:'flex', justifyContent:'space-between', alignItems:'center', marginTop:'10px' },
};

// ── Helpers ──────────────────────────────────────────────────
const hoy      = () => new Date().toISOString().slice(0,10);
const fmtFecha = (f) => f ? new Date(f+'T00:00:00').toLocaleDateString('es-SV') : '—';
const fmtDT    = (f) => f ? new Date(f).toLocaleString('es-SV') : '—';
const fmtMonto = (n) => `$${Number(n||0).toFixed(2)}`;
const periodoActual = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}`;
};

const ESTADO_COLOR = {
  Activo:        C.verde,
  Mantenimiento: C.naranja,
  Baja:          C.gris,
  Vendido:       C.morado,
};

const CATEGORIAS = ['Equipo médico', 'Mobiliario y equipo de oficina', 'Equipo de cómputo', 'Vehículo', 'Equipo de climatización', 'Otro'];

function exportarCSV(datos, columnas, nombreArchivo) {
  const enc = (v) => {
    if (v == null) return '';
    const s = String(v);
    return s.includes(',') || s.includes('"') || s.includes('\n') ? `"${s.replace(/"/g,'""')}"` : s;
  };
  const cabecera = columnas.map(c => enc(c.label)).join(',');
  const filas = datos.map(row => columnas.map(c => enc(row[c.key])).join(','));
  const csv = [cabecera, ...filas].join('\n');
  const blob = new Blob(['\uFEFF'+csv], { type:'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url; a.download = `${nombreArchivo}.csv`; a.click();
  URL.revokeObjectURL(url);
}

function imprimirHTML(html, titulo) {
  const w = window.open('','_blank','width=900,height=700');
  w.document.write(`<!DOCTYPE html><html lang="es"><head>
    <meta charset="UTF-8"/><title>${titulo}</title>
    <style>
      * { box-sizing:border-box; margin:0; padding:0; }
      body { font-family:Segoe UI,sans-serif; padding:28px 32px; color:#222; font-size:12px; }
      h1 { color:#1B2A5E; font-size:17px; margin-bottom:4px; }
      .sub { color:#6c757d; font-size:11px; margin-bottom:16px; }
      .linea { height:2px; background:linear-gradient(90deg,#1B2A5E,#1D9E75); margin-bottom:16px; }
      table { width:100%; border-collapse:collapse; font-size:11px; }
      th { background:#1B2A5E; color:#fff; padding:7px 8px; text-align:left; font-weight:600; }
      td { padding:6px 8px; border-bottom:1px solid #eee; }
      tr:nth-child(even) td { background:#f8fafd; }
      .total-box { background:#1B2A5E; color:#fff; padding:10px 16px; margin-top:12px;
                   border-radius:6px; display:flex; justify-content:space-between; }
      .total-box span:last-child { font-size:16px; font-weight:800; }
      @media print { body { padding:12px; } }
    </style>
  </head><body>
    <h1>ALTIMO Medical Center</h1>
    <div class="sub">Generado: ${new Date().toLocaleString('es-SV')}</div>
    <div class="linea"></div>
    ${html}
    <script>window.onload=()=>{ setTimeout(()=>window.print(),400); }<\/script>
  </body></html>`);
  w.document.close();
}

const ACTIVO_VACIO = {
  codigo_activo:'', nombre:'', categoria:'', ubicacion:'', custodio:'',
  proveedor:'', numero_factura:'', fecha_adquisicion: hoy(),
  valor_adquisicion:'', valor_residual:0, vida_util_meses:'', observaciones:'',
};

const MOV_VACIO = { tipo_mov:'Traslado', ubicacion_nueva:'', custodio_nuevo:'', observacion:'' };
const BAJA_VACIO = { fecha_baja: hoy(), valor_venta:0, motivo:'' };

// ════════════════════════════════════════════════════════════
//  COMPONENTE PRINCIPAL
// ════════════════════════════════════════════════════════════
export default function ActivosFijos({ usuario }) {
  const [vista, setVista] = useState('listado'); // listado | cierre | reportes

  return (
    <div style={{ fontFamily:'Segoe UI, sans-serif' }}>
      <div style={{ background:'linear-gradient(135deg,#1B2A5E,#0f1c40)', padding:'16px 24px 0' }}>
        <div style={{ fontSize:'19px', fontWeight:'800', color:'#fff', marginBottom:'3px' }}>
          🏷️ Módulo de Activo Fijo
        </div>
        <div style={{ fontSize:'12px', color:'rgba(255,255,255,.8)', marginBottom:'12px' }}>
          Catálogo · Kardex · Depreciación (línea recta) · Bajas
        </div>
        <div style={{ display:'flex', gap:'2px' }}>
          {[
            { id:'listado',  label:'📋 Catálogo' },
            { id:'cierre',   label:'🔒 Cierre de período' },
            { id:'reportes', label:'📑 Depreciación' },
          ].map(t => (
            <button key={t.id} onClick={() => setVista(t.id)}
              style={{ padding:'9px 20px', border:'none', cursor:'pointer', fontSize:'13px', fontWeight:'700',
                borderRadius:'8px 8px 0 0', fontFamily:'inherit',
                background: vista === t.id ? '#fff' : 'rgba(255,255,255,.15)',
                color:      vista === t.id ? '#1B2A5E' : '#fff' }}>
              {t.label}
            </button>
          ))}
        </div>
      </div>

      <div style={S.pagina}>
        {vista === 'listado'  && <SeccionListado usuario={usuario} />}
        {vista === 'cierre'   && <SeccionCierre usuario={usuario} />}
        {vista === 'reportes' && <SeccionReporteDepreciacion />}
      </div>
    </div>
  );
}

// ════════════════════════════════════════════════════════════
//  1. CATÁLOGO — listado, alta/edición, kardex, baja
// ════════════════════════════════════════════════════════════
function SeccionListado({ usuario }) {
  const [activos,    setActivos]    = useState([]);
  const [busqueda,   setBusqueda]   = useState('');
  const [filtroEst,  setFiltroEst]  = useState('Todos');
  const [cargando,   setCargando]   = useState(false);
  const [mensaje,    setMensaje]    = useState(null);
  const [guardando,  setGuardando]  = useState(false);

  const [modalActivo, setModalActivo] = useState(null); // null | 'nuevo' | activo
  const [formActivo,  setFormActivo]  = useState(ACTIVO_VACIO);
  const [modalDetalle, setModalDetalle] = useState(null); // null | { activo, movimientos, depreciaciones }
  const [modalMov,    setModalMov]    = useState(null); // null | activo
  const [formMov,     setFormMov]     = useState(MOV_VACIO);
  const [modalBaja,   setModalBaja]   = useState(null); // null | activo
  const [formBaja,    setFormBaja]    = useState(BAJA_VACIO);

  const aviso = (tipo, texto) => { setMensaje({ tipo, texto }); setTimeout(() => setMensaje(null), 4000); };

  const cargar = useCallback(async () => {
    setCargando(true);
    const filtros = {};
    if (filtroEst !== 'Todos') filtros.estado = filtroEst;
    if (busqueda.trim()) filtros.texto = busqueda.trim();
    const r = await api.activosFijos.listar(filtros);
    if (r.ok) setActivos(r.data);
    setCargando(false);
  }, [filtroEst, busqueda]);

  useEffect(() => { cargar(); }, [cargar]);

  // ── Alta / edición ───────────────────────────────────────────
  const abrirNuevo = () => { setFormActivo(ACTIVO_VACIO); setModalActivo('nuevo'); setMensaje(null); };
  const abrirEditar = (a) => {
    setFormActivo({
      nombre:a.nombre, categoria:a.categoria, valor_adquisicion:a.valor_adquisicion,
      vida_util_meses:a.vida_util_meses, valor_residual:a.valor_residual, observaciones:a.observaciones||'',
    });
    setModalActivo(a); setMensaje(null);
  };

  const guardarActivo = async () => {
    if (modalActivo === 'nuevo') {
      if (!formActivo.codigo_activo || !formActivo.nombre || !formActivo.categoria || !formActivo.valor_adquisicion || !formActivo.vida_util_meses) {
        aviso('err','Código, nombre, categoría, valor de adquisición y vida útil son obligatorios'); return;
      }
    }
    setGuardando(true);
    const payload = {
      ...formActivo,
      valor_adquisicion: Number(formActivo.valor_adquisicion)||0,
      valor_residual:     Number(formActivo.valor_residual)||0,
      vida_util_meses:    Number(formActivo.vida_util_meses)||0,
      usuario_id: usuario.id,
    };
    const r = modalActivo === 'nuevo'
      ? await api.activosFijos.crear(payload)
      : await api.activosFijos.editar({ ...payload, id: modalActivo.id });
    setGuardando(false);
    if (r.ok) { setModalActivo(null); cargar(); aviso('ok', modalActivo==='nuevo' ? 'Activo registrado' : 'Activo actualizado'); }
    else        aviso('err', r.error);
  };

  // ── Detalle (kardex + depreciaciones del activo) ─────────────
  const abrirDetalle = async (a) => {
    const r = await api.activosFijos.obtener(a.id);
    if (r.ok) setModalDetalle(r.data);
  };

  // ── Movimiento (traslado / mantenimiento / ajuste) ───────────
  const abrirMov = (a) => { setFormMov({ ...MOV_VACIO, ubicacion_nueva:a.ubicacion||'', custodio_nuevo:a.custodio||'' }); setModalMov(a); };
  const guardarMov = async () => {
    setGuardando(true);
    const r = await api.activosFijos.movimiento(modalMov.id, { ...formMov, usuario_id: usuario.id });
    setGuardando(false);
    if (r.ok) { setModalMov(null); cargar(); aviso('ok','Movimiento registrado'); }
    else        aviso('err', r.error);
  };

  // ── Baja / venta ──────────────────────────────────────────────
  const abrirBaja = (a) => { setFormBaja(BAJA_VACIO); setModalBaja(a); };
  const guardarBaja = async () => {
    if (!formBaja.motivo.trim()) { aviso('err','El motivo de la baja es obligatorio'); return; }
    setGuardando(true);
    const r = await api.activosFijos.baja(modalBaja.id, { ...formBaja, valor_venta: Number(formBaja.valor_venta)||0, usuario_id: usuario.id });
    setGuardando(false);
    if (r.ok) {
      setModalBaja(null); cargar();
      const signo = r.data.utilidadPerdida >= 0 ? 'Utilidad' : 'Pérdida';
      aviso('ok', `Activo dado de baja. ${signo}: ${fmtMonto(Math.abs(r.data.utilidadPerdida))}`);
    } else aviso('err', r.error);
  };

  const totalLibros = activos.filter(a=>a.estado!=='Baja').reduce((s,a)=>s+Number(a.valor_libros||0),0);

  return (
    <div>
      {mensaje && <div style={S.alerta(mensaje.tipo)}>{mensaje.texto}</div>}

      <div style={S.card}>
        <div style={S.cardTit}>
          <span>Catálogo de activos ({activos.length})</span>
          <button style={S.btnVerde} onClick={abrirNuevo}>+ Nuevo activo</button>
        </div>

        <div style={{ ...S.fila3, marginBottom:'14px' }}>
          <div style={S.campo}>
            <label style={S.label}>Buscar</label>
            <input style={S.input} placeholder="Nombre o código..." value={busqueda} onChange={e=>setBusqueda(e.target.value)} />
          </div>
          <div style={S.campo}>
            <label style={S.label}>Estado</label>
            <select style={S.input} value={filtroEst} onChange={e=>setFiltroEst(e.target.value)}>
              {['Todos','Activo','Mantenimiento','Baja','Vendido'].map(o=><option key={o} value={o}>{o}</option>)}
            </select>
          </div>
        </div>

        {cargando && <div style={{ textAlign:'center', color:C.gris, padding:'20px' }}>Cargando...</div>}

        {!cargando && (
          <div style={{ overflowX:'auto' }}>
            <table style={S.tabla}>
              <thead>
                <tr>
                  {['Código','Nombre','Categoría','Ubicación','Adquisición','Valor original','Dep. acumulada','Valor en libros','Estado','Acciones'].map(h=>(
                    <th key={h} style={S.th}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {activos.length === 0 ? (
                  <tr><td colSpan={10} style={{ ...S.td, textAlign:'center', color:C.gris }}>Sin activos registrados.</td></tr>
                ) : activos.map(a => (
                  <tr key={a.id}>
                    <td style={{ ...S.td, fontWeight:'700', color:C.azul }}>{a.codigo_activo}</td>
                    <td style={S.td}>{a.nombre}</td>
                    <td style={{ ...S.td, fontSize:'12px' }}>{a.categoria}</td>
                    <td style={{ ...S.td, fontSize:'12px' }}>{a.ubicacion||'—'}</td>
                    <td style={{ ...S.td, fontSize:'11px', color:C.gris }}>{fmtFecha(a.fecha_adquisicion)}</td>
                    <td style={{ ...S.td, textAlign:'right' }}>{fmtMonto(a.valor_adquisicion)}</td>
                    <td style={{ ...S.td, textAlign:'right', color:C.naranja }}>{fmtMonto(a.depreciacion_acumulada)}</td>
                    <td style={{ ...S.td, textAlign:'right', fontWeight:'700', color:C.verde }}>{fmtMonto(a.valor_libros)}</td>
                    <td style={S.td}><span style={S.badge(ESTADO_COLOR[a.estado])}>{a.estado}</span></td>
                    <td style={S.td}>
                      <div style={{ display:'flex', gap:'4px', flexWrap:'wrap' }}>
                        <button style={S.btnSm()} onClick={()=>abrirDetalle(a)}>Kardex</button>
                        {a.estado !== 'Baja' && a.estado !== 'Vendido' && <>
                          <button style={S.btnSm()} onClick={()=>abrirEditar(a)}>Editar</button>
                          <button style={S.btnSm(C.azul)} onClick={()=>abrirMov(a)}>Mover</button>
                          <button style={S.btnSm(C.rojo)} onClick={()=>abrirBaja(a)}>Baja</button>
                        </>}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {!cargando && activos.length > 0 && (
          <div style={S.totalBox}>
            <span style={{ color:'#fff', fontSize:'13px' }}>Valor en libros total (activos no dados de baja)</span>
            <span style={{ color:'#fff', fontSize:'18px', fontWeight:'800' }}>{fmtMonto(totalLibros)}</span>
          </div>
        )}
      </div>

      {/* ── MODAL: Nuevo / editar activo ── */}
      {modalActivo !== null && (
        <div style={S.modal} onClick={()=>setModalActivo(null)}>
          <div style={S.modalCard('560px')} onClick={e=>e.stopPropagation()}>
            <h3 style={{ color:C.azul, marginBottom:'16px' }}>{modalActivo==='nuevo' ? 'Nuevo activo' : `Editar: ${modalActivo.codigo_activo}`}</h3>

            {modalActivo === 'nuevo' && (
              <div style={S.fila2}>
                <div style={S.campo}>
                  <label style={S.label}>Código de activo *</label>
                  <input style={S.input} value={formActivo.codigo_activo} onChange={e=>setFormActivo({...formActivo, codigo_activo:e.target.value})} placeholder="AF-2026-00001" />
                </div>
                <div style={S.campo}>
                  <label style={S.label}>Fecha de adquisición *</label>
                  <input type="date" style={S.input} value={formActivo.fecha_adquisicion} onChange={e=>setFormActivo({...formActivo, fecha_adquisicion:e.target.value})} />
                </div>
              </div>
            )}

            <div style={S.campo}>
              <label style={S.label}>Nombre *</label>
              <input style={S.input} value={formActivo.nombre} onChange={e=>setFormActivo({...formActivo, nombre:e.target.value})} />
            </div>

            <div style={S.fila2}>
              <div style={S.campo}>
                <label style={S.label}>Categoría *</label>
                <select style={S.input} value={formActivo.categoria} onChange={e=>setFormActivo({...formActivo, categoria:e.target.value})}>
                  <option value="">Seleccionar...</option>
                  {CATEGORIAS.map(c=><option key={c} value={c}>{c}</option>)}
                </select>
              </div>
              {modalActivo === 'nuevo' && (
                <div style={S.campo}>
                  <label style={S.label}>Ubicación</label>
                  <input style={S.input} value={formActivo.ubicacion} onChange={e=>setFormActivo({...formActivo, ubicacion:e.target.value})} />
                </div>
              )}
            </div>

            {modalActivo === 'nuevo' && (
              <div style={S.fila2}>
                <div style={S.campo}>
                  <label style={S.label}>Custodio responsable</label>
                  <input style={S.input} value={formActivo.custodio} onChange={e=>setFormActivo({...formActivo, custodio:e.target.value})} />
                </div>
                <div style={S.campo}>
                  <label style={S.label}>Proveedor</label>
                  <input style={S.input} value={formActivo.proveedor} onChange={e=>setFormActivo({...formActivo, proveedor:e.target.value})} />
                </div>
              </div>
            )}

            <div style={S.fila3}>
              <div style={S.campo}>
                <label style={S.label}>Valor de adquisición *</label>
                <input type="number" step="0.01" style={S.input} value={formActivo.valor_adquisicion}
                  disabled={modalActivo!=='nuevo' && modalActivo._bloqueado}
                  onChange={e=>setFormActivo({...formActivo, valor_adquisicion:e.target.value})} />
              </div>
              <div style={S.campo}>
                <label style={S.label}>Valor residual</label>
                <input type="number" step="0.01" style={S.input} value={formActivo.valor_residual}
                  onChange={e=>setFormActivo({...formActivo, valor_residual:e.target.value})} />
              </div>
              <div style={S.campo}>
                <label style={S.label}>Vida útil (meses) *</label>
                <input type="number" style={S.input} value={formActivo.vida_util_meses}
                  onChange={e=>setFormActivo({...formActivo, vida_util_meses:e.target.value})} />
              </div>
            </div>

            {modalActivo === 'nuevo' && (
              <div style={S.campo}>
                <label style={S.label}>N° de factura</label>
                <input style={S.input} value={formActivo.numero_factura} onChange={e=>setFormActivo({...formActivo, numero_factura:e.target.value})} />
              </div>
            )}

            <div style={S.campo}>
              <label style={S.label}>Observaciones</label>
              <textarea style={{ ...S.input, minHeight:'60px', resize:'vertical' }} value={formActivo.observaciones}
                onChange={e=>setFormActivo({...formActivo, observaciones:e.target.value})} />
            </div>

            <div style={{ display:'flex', gap:'8px', justifyContent:'flex-end', marginTop:'10px' }}>
              <button style={S.btnGris} onClick={()=>setModalActivo(null)}>Cancelar</button>
              <button style={S.btnVerde} disabled={guardando} onClick={guardarActivo}>{guardando ? 'Guardando...' : 'Guardar'}</button>
            </div>
          </div>
        </div>
      )}

      {/* ── MODAL: Detalle (kardex + depreciaciones) ── */}
      {modalDetalle && (
        <div style={S.modal} onClick={()=>setModalDetalle(null)}>
          <div style={S.modalCard('640px')} onClick={e=>e.stopPropagation()}>
            <h3 style={{ color:C.azul, marginBottom:'4px' }}>{modalDetalle.codigo_activo} — {modalDetalle.nombre}</h3>
            <p style={{ fontSize:'12px', color:C.gris, marginBottom:'16px' }}>
              Valor en libros actual: <strong style={{ color:C.verde }}>{fmtMonto(modalDetalle.valor_libros)}</strong> ·
              {' '}Dep. acumulada: <strong style={{ color:C.naranja }}>{fmtMonto(modalDetalle.depreciacion_acumulada)}</strong>
            </p>

            <div style={{ fontSize:'12px', fontWeight:'700', color:C.azul, marginBottom:'8px' }}>Kardex de movimientos</div>
            <div style={{ overflowX:'auto', marginBottom:'18px' }}>
              <table style={S.tabla}>
                <thead><tr>{['Fecha','Tipo','Ubicación','Custodio','Usuario'].map(h=><th key={h} style={S.th}>{h}</th>)}</tr></thead>
                <tbody>
                  {modalDetalle.movimientos.length===0 ? (
                    <tr><td colSpan={5} style={{ ...S.td, textAlign:'center', color:C.gris }}>Sin movimientos.</td></tr>
                  ) : modalDetalle.movimientos.map(m=>(
                    <tr key={m.id}>
                      <td style={{ ...S.td, fontSize:'11px' }}>{fmtDT(m.registrado_en)}</td>
                      <td style={S.td}><span style={S.badge(C.azul)}>{m.tipo_mov}</span></td>
                      <td style={{ ...S.td, fontSize:'12px' }}>{m.ubicacion_nueva||'—'}</td>
                      <td style={{ ...S.td, fontSize:'12px' }}>{m.custodio_nuevo||'—'}</td>
                      <td style={{ ...S.td, fontSize:'11px', color:C.gris }}>{m.usuario_nombre||'—'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <div style={{ fontSize:'12px', fontWeight:'700', color:C.azul, marginBottom:'8px' }}>Depreciación cerrada por período</div>
            <div style={{ overflowX:'auto' }}>
              <table style={S.tabla}>
                <thead><tr>{['Período','Dep. del mes','Dep. acumulada','Valor en libros'].map(h=><th key={h} style={S.th}>{h}</th>)}</tr></thead>
                <tbody>
                  {modalDetalle.depreciaciones.length===0 ? (
                    <tr><td colSpan={4} style={{ ...S.td, textAlign:'center', color:C.gris }}>Sin períodos cerrados aún.</td></tr>
                  ) : modalDetalle.depreciaciones.map(d=>(
                    <tr key={d.id}>
                      <td style={{ ...S.td, fontWeight:'700' }}>{d.periodo}</td>
                      <td style={{ ...S.td, textAlign:'right' }}>{fmtMonto(d.depreciacion_periodo)}</td>
                      <td style={{ ...S.td, textAlign:'right', color:C.naranja }}>{fmtMonto(d.depreciacion_acumulada)}</td>
                      <td style={{ ...S.td, textAlign:'right', fontWeight:'700', color:C.verde }}>{fmtMonto(d.valor_libros_final)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <div style={{ display:'flex', justifyContent:'flex-end', marginTop:'14px' }}>
              <button style={S.btnGris} onClick={()=>setModalDetalle(null)}>Cerrar</button>
            </div>
          </div>
        </div>
      )}

      {/* ── MODAL: Movimiento ── */}
      {modalMov && (
        <div style={S.modal} onClick={()=>setModalMov(null)}>
          <div style={S.modalCard('440px')} onClick={e=>e.stopPropagation()}>
            <h3 style={{ color:C.azul, marginBottom:'16px' }}>Movimiento — {modalMov.codigo_activo}</h3>
            <div style={S.campo}>
              <label style={S.label}>Tipo de movimiento</label>
              <select style={S.input} value={formMov.tipo_mov} onChange={e=>setFormMov({...formMov, tipo_mov:e.target.value})}>
                {['Traslado','Mantenimiento','Ajuste'].map(o=><option key={o} value={o}>{o}</option>)}
              </select>
            </div>
            <div style={S.fila2}>
              <div style={S.campo}>
                <label style={S.label}>Nueva ubicación</label>
                <input style={S.input} value={formMov.ubicacion_nueva} onChange={e=>setFormMov({...formMov, ubicacion_nueva:e.target.value})} />
              </div>
              <div style={S.campo}>
                <label style={S.label}>Nuevo custodio</label>
                <input style={S.input} value={formMov.custodio_nuevo} onChange={e=>setFormMov({...formMov, custodio_nuevo:e.target.value})} />
              </div>
            </div>
            <div style={S.campo}>
              <label style={S.label}>Observación</label>
              <textarea style={{ ...S.input, minHeight:'60px' }} value={formMov.observacion} onChange={e=>setFormMov({...formMov, observacion:e.target.value})} />
            </div>
            <div style={{ display:'flex', gap:'8px', justifyContent:'flex-end' }}>
              <button style={S.btnGris} onClick={()=>setModalMov(null)}>Cancelar</button>
              <button style={S.btnAzul} disabled={guardando} onClick={guardarMov}>{guardando ? 'Guardando...' : 'Registrar'}</button>
            </div>
          </div>
        </div>
      )}

      {/* ── MODAL: Baja / venta ── */}
      {modalBaja && (
        <div style={S.modal} onClick={()=>setModalBaja(null)}>
          <div style={S.modalCard('440px')} onClick={e=>e.stopPropagation()}>
            <h3 style={{ color:C.rojo, marginBottom:'4px' }}>Dar de baja — {modalBaja.codigo_activo}</h3>
            <p style={{ fontSize:'12px', color:C.gris, marginBottom:'16px' }}>
              Valor en libros actual: <strong>{fmtMonto(modalBaja.valor_libros)}</strong>. Esta acción es irreversible.
            </p>
            <div style={S.fila2}>
              <div style={S.campo}>
                <label style={S.label}>Fecha de baja</label>
                <input type="date" style={S.input} value={formBaja.fecha_baja} onChange={e=>setFormBaja({...formBaja, fecha_baja:e.target.value})} />
              </div>
              <div style={S.campo}>
                <label style={S.label}>Valor de venta (0 si es retiro)</label>
                <input type="number" step="0.01" style={S.input} value={formBaja.valor_venta} onChange={e=>setFormBaja({...formBaja, valor_venta:e.target.value})} />
              </div>
            </div>
            <div style={S.campo}>
              <label style={S.label}>Motivo *</label>
              <textarea style={{ ...S.input, minHeight:'60px' }} value={formBaja.motivo} onChange={e=>setFormBaja({...formBaja, motivo:e.target.value})} placeholder="Obsolescencia, daño irreparable, venta a terceros..." />
            </div>
            <div style={{ display:'flex', gap:'8px', justifyContent:'flex-end' }}>
              <button style={S.btnGris} onClick={()=>setModalBaja(null)}>Cancelar</button>
              <button style={S.btnRojo} disabled={guardando} onClick={guardarBaja}>{guardando ? 'Procesando...' : 'Confirmar baja'}</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ════════════════════════════════════════════════════════════
//  2. CIERRE DE PERÍODO — genera el snapshot contable oficial
// ════════════════════════════════════════════════════════════
function SeccionCierre({ usuario }) {
  const [periodo,    setPeriodo]    = useState(periodoActual());
  const [procesando, setProcesando] = useState(false);
  const [resultado,  setResultado]  = useState(null);
  const [mensaje,    setMensaje]    = useState(null);

  const cerrar = async () => {
    if (!window.confirm(`¿Cerrar el período ${periodo}? Esta acción genera el registro contable oficial y no se puede deshacer.`)) return;
    setProcesando(true); setResultado(null);
    const r = await api.activosFijos.cerrarPeriodo({ periodo, usuario_id: usuario.id });
    setProcesando(false);
    if (r.ok) { setResultado(r.data); setMensaje({ tipo:'ok', texto:'Período cerrado correctamente' }); }
    else        setMensaje({ tipo:'err', texto:r.error });
  };

  return (
    <div style={S.card}>
      <div style={S.cardTit}>Cierre mensual de depreciación</div>

      <div style={{ background:'#fff3cd', borderRadius:'8px', padding:'12px 14px', marginBottom:'16px', fontSize:'12px', color:'#856404' }}>
        ⚠️ Genera el snapshot contable oficial (línea recta) de todos los activos en estado <strong>Activo</strong> o <strong>Mantenimiento</strong>.
        Una vez cerrado un período, no puede regenerarse ni modificarse — es el dato que alimenta los reportes NIIF.
      </div>

      {mensaje && <div style={S.alerta(mensaje.tipo)}>{mensaje.texto}</div>}

      <div style={{ ...S.fila2, alignItems:'flex-end', maxWidth:'420px' }}>
        <div style={S.campo}>
          <label style={S.label}>Período a cerrar</label>
          <input type="month" style={S.input} value={periodo} onChange={e=>setPeriodo(e.target.value)} />
        </div>
        <button style={S.btnRojo} disabled={procesando} onClick={cerrar}>
          {procesando ? 'Cerrando...' : '🔒 Cerrar período'}
        </button>
      </div>

      {resultado && (
        <div style={{ ...S.totalBox, marginTop:'20px', flexDirection:'column', alignItems:'flex-start', gap:'6px' }}>
          <span style={{ color:'#fff', fontSize:'13px' }}>Período {resultado.periodo} cerrado · {resultado.procesados} activo(s) depreciados</span>
          <span style={{ color:'#fff', fontSize:'20px', fontWeight:'800' }}>{fmtMonto(resultado.totalDepreciado)} depreciado este mes</span>
        </div>
      )}
    </div>
  );
}

// ════════════════════════════════════════════════════════════
//  3. REPORTE — depreciación oficial por período (snapshot)
// ════════════════════════════════════════════════════════════
function SeccionReporteDepreciacion() {
  const [periodo,  setPeriodo]  = useState(periodoActual());
  const [datos,    setDatos]    = useState(null);
  const [cargando, setCargando] = useState(false);
  const [buscado,  setBuscado]  = useState(false);

  const cargar = async () => {
    setCargando(true); setBuscado(true);
    const r = await api.activosFijos.reporteDepreciacion(periodo);
    if (r.ok) setDatos(r.data);
    setCargando(false);
  };

  const COLS_CSV = [
    { key:'codigo_activo',          label:'Código' },
    { key:'nombre',                 label:'Activo' },
    { key:'categoria',              label:'Categoría' },
    { key:'valor_libros_inicial',   label:'Valor libros inicial ($)' },
    { key:'depreciacion_periodo',   label:'Depreciación del mes ($)' },
    { key:'depreciacion_acumulada', label:'Depreciación acumulada ($)' },
    { key:'valor_libros_final',     label:'Valor libros final ($)' },
  ];

  const imprimirPDF = () => {
    if (!datos) return;
    const filas = datos.detalle.map(d => `<tr>
      <td>${d.codigo_activo}</td><td>${d.nombre}</td><td>${d.categoria}</td>
      <td style="text-align:right">${fmtMonto(d.valor_libros_inicial)}</td>
      <td style="text-align:right">${fmtMonto(d.depreciacion_periodo)}</td>
      <td style="text-align:right">${fmtMonto(d.depreciacion_acumulada)}</td>
      <td style="text-align:right;font-weight:700">${fmtMonto(d.valor_libros_final)}</td>
    </tr>`).join('');
    imprimirHTML(`
      <h2>Depreciación de Activos Fijos — Período ${datos.periodo}</h2>
      <p style="color:#6c757d;margin-bottom:12px">${datos.detalle.length} activo(s) depreciados este período</p>
      <table>
        <thead><tr><th>Código</th><th>Activo</th><th>Categoría</th><th>Valor inicial</th><th>Dep. del mes</th><th>Dep. acumulada</th><th>Valor final</th></tr></thead>
        <tbody>${filas}</tbody>
      </table>
      <div class="total-box">
        <span>Total depreciado en el período</span>
        <span>${fmtMonto(datos.totales.depreciacion_periodo)}</span>
      </div>
    `, `Depreciacion_${datos.periodo}`);
  };

  return (
    <div style={S.card}>
      <div style={S.cardTit}>
        <span>Reporte de depreciación (cifra contable oficial)</span>
        <div style={{ display:'flex', gap:'8px', alignItems:'flex-end', flexWrap:'wrap' }}>
          <div style={S.campo}>
            <label style={S.label}>Período</label>
            <input type="month" style={S.input} value={periodo} onChange={e=>setPeriodo(e.target.value)} />
          </div>
          <button style={S.btnAzul} onClick={cargar}>Generar</button>
          {datos?.detalle.length>0 && <>
            <button style={S.btnVerde} onClick={()=>exportarCSV(datos.detalle,COLS_CSV,`Depreciacion_${periodo}`)}>⬇ Excel</button>
            <button style={S.btnNaranja} onClick={imprimirPDF}>🖨 PDF</button>
          </>}
        </div>
      </div>

      {cargando && <div style={{ textAlign:'center', color:C.gris, padding:'20px' }}>Generando...</div>}
      {!cargando && buscado && datos?.detalle.length === 0 && (
        <p style={{ color:C.gris, fontSize:'13px' }}>Este período aún no ha sido cerrado, o no tuvo activos por depreciar.</p>
      )}

      {!cargando && datos?.detalle.length > 0 && (
        <>
          <div style={{ overflowX:'auto', marginBottom:'14px' }}>
            <table style={S.tabla}>
              <thead>
                <tr>{['Código','Activo','Categoría','Valor inicial','Dep. del mes','Dep. acumulada','Valor final'].map(h=><th key={h} style={S.th}>{h}</th>)}</tr>
              </thead>
              <tbody>
                {datos.detalle.map(d => (
                  <tr key={d.id}>
                    <td style={{ ...S.td, fontWeight:'700', color:C.azul }}>{d.codigo_activo}</td>
                    <td style={S.td}>{d.nombre}</td>
                    <td style={{ ...S.td, fontSize:'12px' }}>{d.categoria}</td>
                    <td style={{ ...S.td, textAlign:'right' }}>{fmtMonto(d.valor_libros_inicial)}</td>
                    <td style={{ ...S.td, textAlign:'right', color:C.naranja }}>{fmtMonto(d.depreciacion_periodo)}</td>
                    <td style={{ ...S.td, textAlign:'right' }}>{fmtMonto(d.depreciacion_acumulada)}</td>
                    <td style={{ ...S.td, textAlign:'right', fontWeight:'700', color:C.verde }}>{fmtMonto(d.valor_libros_final)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div style={S.totalBox}>
            <span style={{ color:'#fff', fontSize:'13px' }}>Total depreciado en {datos.periodo}</span>
            <span style={{ color:'#fff', fontSize:'18px', fontWeight:'800' }}>{fmtMonto(datos.totales.depreciacion_periodo)}</span>
          </div>
        </>
      )}
    </div>
  );
}
